// src/components/resources/tools/generators/CommentThread/previewShared.tsx

'use client'

import type { FC } from 'react'
import { clsx } from 'clsx'
import { avatarInitial, normalizeHandle, type ThreadComment } from './threadModel'

/** Props every per-platform comment preview receives. */
export interface CommentPreviewProps {
  /** Top-level comments, already pin-ordered by the caller (`orderThread`). */
  comments: ThreadComment[]
  /** Post author's handle — drives "Pinned by …" labels and author badges. */
  postAuthor: string
  theme: 'light' | 'dark'
  /**
   * Toggle a comment's viewer-liked state. Wired to each platform's like
   * affordance (heart / thumb) so clicking IN the preview likes the comment —
   * the same direct-preview interaction as the Reddit post card's vote pill.
   */
  onToggleLike?: (commentId: string) => void
}

/** Shared accessible name for the in-preview like buttons. */
export function likeButtonLabel(liked: boolean, username: string): string {
  const who = normalizeHandle(username) || 'user'
  return `${liked ? 'Unlike' : 'Like'} comment by ${who}`
}

/** Per-platform avatar gradient so the initials circles read on-brand at a glance. */
const AVATAR_GRADIENTS: Record<string, string> = {
  instagram: 'from-purple-500 to-pink-500',
  youtube: 'from-red-500 to-rose-600',
  tiktok: 'from-cyan-400 to-fuchsia-500',
  x: 'from-sky-500 to-blue-600',
  reddit: 'from-orange-500 to-rose-500',
}

/**
 * Initials avatar. Derives the initial via {@link avatarInitial}, which
 * normalizes the handle FIRST — so `@user` and `user` render the same circle
 * (the strip-after-pick ordering bug this family designs out).
 */
export const CommentAvatar: FC<{
  username: string
  size?: 'sm' | 'md'
  flavor: keyof typeof AVATAR_GRADIENTS
}> = ({ username, size = 'md', flavor }) => (
  <div
    aria-hidden="true"
    className={clsx(
      'flex flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br font-bold text-white',
      AVATAR_GRADIENTS[flavor],
      size === 'md' ? 'h-8 w-8 text-[13px]' : 'h-6 w-6 text-[11px]',
    )}
  >
    {avatarInitial(username)}
  </div>
)

/** Blue-check verified badge shared by the comment previews (IG/YouTube/X styling via `className`). */
export const VerifiedCheck: FC<{ className?: string }> = ({ className }) => (
  <svg
    aria-label="Verified"
    role="img"
    viewBox="0 0 24 24"
    className={clsx('h-3.5 w-3.5 flex-shrink-0', className)}
  >
    <path
      fill="currentColor"
      d="M12 1.5l2.61 2.16 3.38-.4 1.03 3.25 3.02 1.58-1.15 3.21L22.5 12l-1.61 2.7 1.15 3.21-3.02 1.58-1.03 3.25-3.38-.4L12 24.5l-2.61-2.16-3.38.4-1.03-3.25-3.02-1.58 1.15-3.21L1.5 12l1.61-2.7-1.15-3.21 3.02-1.58L6.01 1.26l3.38.4L12 1.5z"
      transform="scale(0.92) translate(1, -1)"
    />
    <path
      fill="#fff"
      d="M10.5 15.6l-3.1-3.1 1.27-1.27 1.83 1.83 4.83-4.83 1.27 1.27-6.1 6.1z"
    />
  </svg>
)
